import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Legend
} from "recharts";
import moment from "moment";
import Loading from "../../../Shared/Loading";

const DoctorStatistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch("https://servercareseebd.careseebd.com/api/v1/user/doctor-stats", {
      headers: {
        authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch statistics");
        }
        return res.json();
      })
      .then((data) => {
        setStats(data?.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md my-5">
        <div className="flex items-center">
          <Icon icon="lucide:alert-circle" className="text-red-500 text-2xl mr-2" />
          <p className="text-red-700 font-semibold">Error loading statistics: {error}</p>
        </div>
      </div>
    );
  }

  const summary = stats?.summary || {
    totalPatients: 0,
    activePatients: 0,
    inactivePatients: 0,
    activePackages: 0,
    expiredPackages: 0,
    totalGameSessions: 0,
  };

  // Prepare chart data for trends
  const trendData = stats?.trends?.labels?.map((label, idx) => ({
    name: label,
    Patients: stats.trends.patients[idx] || 0,
    "Game Sessions": stats.trends.gamePlays[idx] || 0,
  })) || [];

  // Top Active Patients data
  const topPatientsData = stats?.topActivePatients?.map(p => ({
    name: p.userName || "Unknown",
    sessions: p.count,
  })) || [];

  const COLORS = ["#291D89", "#01AEEF", "#10B981", "#F59E0B", "#EF4444"];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Overview Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Patients Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center gap-5 group">
          <div className="p-4 bg-indigo-50 text-secondary rounded-2xl group-hover:scale-110 duration-300">
            <Icon icon="fluent:people-community-24-filled" className="text-3xl" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Patients</p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">{summary.totalPatients}</h3>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
              {summary.activePatients} Active patients
            </p>
          </div>
        </div>

        {/* Active Subscriptions Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center gap-5 group">
          <div className="p-4 bg-cyan-50 text-primary rounded-2xl group-hover:scale-110 duration-300">
            <Icon icon="solar:box-minimalistic-bold" className="text-3xl" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Active Packages</p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">{summary.activePackages}</h3>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
              {summary.expiredPackages} Expired packages
            </p>
          </div>
        </div>

        {/* Game Sessions Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-300 flex items-center gap-5 group">
          <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl group-hover:scale-110 duration-300">
            <Icon icon="lucide:gamepad-2" className="text-3xl" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Game Sessions</p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">{summary.totalGameSessions}</h3>
            <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1 font-semibold">
              <Icon icon="lucide:trending-up" />
              Patient therapy training
            </p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Trend Area Chart */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h4 className="text-lg font-bold text-slate-800">Practice Metrics Trend</h4>
              <p className="text-sm text-slate-500">Monthly patient signs-up & game therapies</p>
            </div>
            <Icon icon="lucide:bar-chart-3" className="text-2xl text-slate-400" />
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorPatients" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#291D89" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#291D89" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorGamePlays" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#01AEEF" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#01AEEF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} />
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                />
                <Legend iconType="circle" wrapperStyle={{ paddingTop: "10px" }} />
                <Area type="monotone" dataKey="Patients" stroke="#291D89" strokeWidth={2} fillOpacity={1} fill="url(#colorPatients)" />
                <Area type="monotone" dataKey="Game Sessions" stroke="#01AEEF" strokeWidth={2} fillOpacity={1} fill="url(#colorGamePlays)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Active Patients Bar Chart */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h4 className="text-lg font-bold text-slate-800">Most Active Patients</h4>
              <p className="text-sm text-slate-500">Top 5 patients by total completed game sessions</p>
            </div>
            <Icon icon="lucide:award" className="text-2xl text-slate-400" />
          </div>
          <div className="h-80">
            {topPatientsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topPatientsData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                    cursor={{ fill: "#f8fafc" }}
                  />
                  <Bar dataKey="sessions" name="Game Sessions" fill="#291D89" radius={[8, 8, 0, 0]}>
                    {topPatientsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400">
                <Icon icon="lucide:gamepad-2" className="text-5xl mb-2 stroke-1" />
                <p>No game records recorded yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Details Tables Section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Recent Registered Patients */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex justify-between items-center mb-5">
            <h4 className="text-lg font-bold text-slate-800">Recent Registrations</h4>
            <span className="text-xs font-semibold px-2.5 py-1 bg-indigo-50 text-secondary rounded-full">New patients</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 text-xs font-semibold uppercase">
                  <th className="pb-3">Name</th>
                  <th className="pb-3">Contact</th>
                  <th className="pb-3">Registered At</th>
                  <th className="pb-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {stats?.recentPatients?.length > 0 ? (
                  stats.recentPatients.map((patient) => (
                    <tr key={patient._id} className="hover:bg-slate-50/50 transition">
                      <td className="py-3 font-semibold text-slate-800">{patient.name}</td>
                      <td className="py-3 text-slate-500">
                        <div>{patient.email}</div>
                        <div className="text-xs text-slate-400">{patient.phone}</div>
                      </td>
                      <td className="py-3 text-slate-500">
                        {moment(patient.createdAt).format("DD MMM YYYY")}
                      </td>
                      <td className="py-3 text-right">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${patient.isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${patient.isActive ? "bg-green-500" : "bg-red-500"}`}></span>
                          {patient.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-10 text-center text-slate-400">No patients registered yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Game Activity Log */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex justify-between items-center mb-5">
            <h4 className="text-lg font-bold text-slate-800">Recent Game Sessions</h4>
            <span className="text-xs font-semibold px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full">Activity stream</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 text-xs font-semibold uppercase">
                  <th className="pb-3">Patient</th>
                  <th className="pb-3">Game</th>
                  <th className="pb-3 text-center">Score</th>
                  <th className="pb-3 text-right">Time Played</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {stats?.recentGameRecords?.length > 0 ? (
                  stats.recentGameRecords.map((record) => (
                    <tr key={record._id} className="hover:bg-slate-50/50 transition">
                      <td className="py-3 font-semibold text-slate-800">{record.userName || "Unknown"}</td>
                      <td className="py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-md font-medium text-xs">
                          {record.gameName}
                        </span>
                      </td>
                      <td className="py-3 text-center font-bold text-secondary">
                        {record.score} <span className="text-xs text-slate-400 font-normal">/ {record.possibleScore || 100}</span>
                      </td>
                      <td className="py-3 text-right text-slate-400 text-xs">
                        {moment(record.createdAt || record.date).fromNow()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-10 text-center text-slate-400">No game activity recorded yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorStatistics;
